import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import filter from 'lodash/filter';
import find from 'lodash/find';
import reject from 'lodash/reject';
import findIndex from 'lodash/findIndex';
import styled, { createGlobalStyle } from 'styled-components';

import { FormSubtitle } from '../styled';
import Geocomplete from './Geocomplete';
import { UAppAddress } from '../../models/AugmentedClasses';
import { OtherPlus } from './ContactForm';
import AddressField from './AddressField';

import { TAppAddress, TAppContact } from 'TProtocol/common/models';

interface Props {
  contact: TAppContact;
  simple?: boolean;
  autocomplete?: boolean;
  onChange: (contact: TAppContact) => void;
  isSingle?: boolean;
}

export enum IUAddressLabel {
  PersonalAddress = 1,
  ProfessionalAddress,
  OtherAddress
}

export type AddressFieldType = 'address1' | 'address2' | 'city' | 'admin1' | 'postalCode' | 'country';

export const AddressLabels: {
  [key in IUAddressLabel]: string;
} = {
  [IUAddressLabel.PersonalAddress]: 'Personal',
  [IUAddressLabel.ProfessionalAddress]: 'Professional',
  [IUAddressLabel.OtherAddress]: 'Other'
};

export interface AddressObject {
  address: UAppAddress;
  label: IUAddressLabel;
}

const AddressFields = ({ contact, simple, onChange, isSingle = false, autocomplete = false }: Props) => {
  const [addresses, setAddresses] = useState<AddressObject[]>([]);

  useEffect(() => {
    const addresses: AddressObject[] = [];
    if (contact.personalAddress) {
      addresses.push({
        address: contact.personalAddress,
        label: IUAddressLabel.PersonalAddress
      });
    }
    if (contact.professionalAddress) {
      addresses.push({
        address: contact.professionalAddress,
        label: IUAddressLabel.ProfessionalAddress
      });
    }

    if (contact.otherAddresses) {
      contact.otherAddresses.forEach((address) => {
        addresses.push({
          address,
          label: IUAddressLabel.OtherAddress
        });
      });
    }

    setAddresses(addresses);
  }, []);

  const handleDelete = (address: AddressObject) => {
    removeAddress(address);
    setAddresses(reject(addresses, address));
    onChange(contact);
  };

  const handleChange = (addressObject: AddressObject, value: string, attribute: AddressFieldType = 'address1') => {
    const address = addressObject.address;

    if ((!address.postalCode || address.postalCode.length <= 0) && (!address.city || address.city.length <= 0)) {
      if (address.country) {
        address.country = '';
      }
      if (address.admin1) {
        address.admin1 = '';
      }
    }

    if (addressObject.label === IUAddressLabel.PersonalAddress && contact.personalAddress) {
      contact.personalAddress[attribute] = value;
    } else if (addressObject.label === IUAddressLabel.ProfessionalAddress && contact.professionalAddress) {
      contact.professionalAddress[attribute] = value;
    } else if (addressObject.label === IUAddressLabel.OtherAddress) {
      const otherAddress = find<UAppAddress>(contact.otherAddresses, { _key: address._key });
      if (otherAddress) {
        otherAddress[attribute] = value;
      }
    }

    onChange(contact);
  };

  // When more than one field updates at the same time, i.e. Google Maps autocomplete in simple mode.
  const handleFullChange = (addressObject: AddressObject) => {
    const address = addressObject.address;

    if (addressObject.label === IUAddressLabel.PersonalAddress && contact.personalAddress) {
      contact.personalAddress = address;
    } else if (addressObject.label === IUAddressLabel.ProfessionalAddress && contact.professionalAddress) {
      contact.professionalAddress = address;
    } else if (addressObject.label === IUAddressLabel.OtherAddress) {
      const otherAddressIndex = findIndex<UAppAddress>(contact.otherAddresses, { _key: address._key });
      if (contact.otherAddresses && otherAddressIndex) {
        contact.otherAddresses.splice(otherAddressIndex, 1, address);
      }
    }

    onChange(contact);
  };

  const handleLabelChange = (address: AddressObject, previousLabel: IUAddressLabel) => {
    removeAddress({
      address: address.address,
      label: previousLabel
    });
    moveExistingAddressToOther(address.label);
    updateAddresses(address);
    updateAddressField(address.label, address.address);
    setAddresses([...addresses]);
    onChange(contact);
  };

  const addAddress = () => {
    const address = new UAppAddress();
    let label = IUAddressLabel.OtherAddress;

    if (!contact.personalAddress) {
      contact.personalAddress = address;
      label = IUAddressLabel.PersonalAddress;
    } else if (!contact.professionalAddress) {
      contact.professionalAddress = address;
      label = IUAddressLabel.ProfessionalAddress;
    } else {
      if (!contact.otherAddresses) {
        contact.otherAddresses = [];
      }
      contact.otherAddresses.push(address);
    }

    addresses.push({ address, label });

    onChange(contact);
  };

  const removeAddress = (address: AddressObject) => {
    if (address.label === IUAddressLabel.OtherAddress) {
      removeOtherAddress(address.address);
    } else {
      updateAddressField(address.label, undefined);
    }
  };

  const removeOtherAddress = (address: TAppAddress) => {
    // Have to find the address by value, as there is no other identifier we can use.
    let found = false;
    contact.otherAddresses = filter(contact.otherAddresses, (otherAddress) => {
      if (!found && address.address1 === otherAddress.address1) {
        found = true;
        return false;
      }
      return true;
    });
  };

  const moveExistingAddressToOther = (label: IUAddressLabel) => {
    let existingAddress: TAppAddress | undefined;
    if (label === IUAddressLabel.PersonalAddress) {
      existingAddress = contact.personalAddress;
    } else if (label === IUAddressLabel.ProfessionalAddress) {
      existingAddress = contact.professionalAddress;
    }

    if (existingAddress !== undefined) {
      if (!contact.otherAddresses) {
        contact.otherAddresses = [];
      }
      contact.otherAddresses.push(existingAddress);
    }

    const existingAddressObject = find(addresses, { label });
    if (existingAddressObject) {
      existingAddressObject.label = IUAddressLabel.OtherAddress;
    }
  };

  const updateAddresses = (address: AddressObject) => {
    const existingAddress = addresses.find((a) => a.address._key === address.address._key);
    if (existingAddress) {
      existingAddress.address = address.address;
      existingAddress.label = address.label;
    }
  };

  const updateAddressField = (label: IUAddressLabel, address: TAppAddress | undefined) => {
    if (label === IUAddressLabel.PersonalAddress) {
      contact.personalAddress = address;
    } else if (label === IUAddressLabel.ProfessionalAddress) {
      contact.professionalAddress = address;
    } else if (label === IUAddressLabel.OtherAddress && address !== undefined) {
      if (!contact.otherAddresses) {
        contact.otherAddresses = [];
      }
      contact.otherAddresses.push(address);
    }
  };

  return <>
    <FormSubtitle>{isSingle ? 'Address' : 'Addresses'}</FormSubtitle>

    <div>
      {
        addresses.map((address) => {
          return <AddressRow key={address.address._key}>
            <Col>
              {simple ?
                <AddressField
                  address={address}
                  hideLabel={isSingle}
                  autocomplete={autocomplete}
                  handleDelete={() => {
                    handleDelete(address);
                  }}
                  handleChange={(value: string, attribute: AddressFieldType) => {
                    handleChange(address, value, attribute);
                  }}
                  handleFullChange={handleFullChange}
                  handleLabelChange={handleLabelChange}
                /> :
                <Geocomplete
                  contact={contact}
                  address={address}
                  autocomplete={autocomplete}
                  handleDelete={() => {
                    handleDelete(address);
                  }}
                  handleChange={(value: string, attribute: AddressFieldType) => {
                    handleChange(address, value, attribute);
                  }}
                  handleLabelChange={handleLabelChange}
                />
              }
            </Col>
          </AddressRow>;
        })
      }
    </div>

    {
      isSingle ? null : <div onClick={addAddress}>
        <OtherPlus>+</OtherPlus> Address
      </div>
    }
  </>;
};

const AddressRow = styled(Row)`
  & > .col > div {
    margin-bottom: 10px;
    border-bottom: 1px solid #333;
  }

  &:last-child > .col > div {
    margin-bottom: 0;
    border-bottom: 0;
  }
`;

createGlobalStyle`
  .pac-container .pac-item {
    cursor: pointer !important;
  }

  .pac-container.pac-logo.hdpi::after {
    display: none !important;
  }
`;

export default AddressFields;
